/**
 * Outil « Tableau de conversion » (Boîte à outils, 6e → 2nde).
 *
 * On tape une valeur, on choisit l'unité de départ et l'unité d'arrivée : le
 * nombre se pose dans le tableau (un chiffre par unité pour les longueurs,
 * masses et contenances, deux pour les aires, trois pour les volumes avec les
 * litres sous dm³ et cm³), la virgule d'arrivée se place, les zéros ajoutés
 * se voient. Dès la 4e, les vitesses : km/h ↔ m/s, par étapes.
 */

import { useId, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { IconButton, Note, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import {
  KINDS,
  approxText,
  MAX_COLUMNS,
  columns,
  convert,
  decText,
  fracToDec,
  kindOf,
  parseDec,
  placeDigits,
  span,
  unitName,
  ruleText,
  speedConvert,
  type Kind,
  type Speed,
  type UnitDef,
} from './units';

type Mode = Kind | 'speed';

const NBSP = String.fromCharCode(0xa0);

/** valeur, unité de départ, unité d'arrivée proposées au départ */
const DEFAULTS: Record<Kind, [string, string, string]> = {
  length: ['3,25', 'm', 'cm'],
  mass: ['2,4', 'kg', 'g'],
  capacity: ['1,5', 'L', 'cL'],
  area: ['2', 'm2', 'cm2'],
  volume: ['1,5', 'L', 'cm3'],
};

function UnitChips({
  units,
  value,
  onChange,
  label,
}: {
  units: readonly UnitDef[];
  value: string;
  onChange: (id: string) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {units.map((u) => (
        <button
          key={u.id}
          type="button"
          aria-pressed={u.id === value}
          aria-label={u.name}
          onClick={() => onChange(u.id)}
          className={cn(
            'min-h-11 min-w-11 rounded-xl border px-2 text-sm font-bold',
            u.id === value
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-700 hover:bg-ink-50',
          )}
        >
          {u.label}
        </button>
      ))}
    </div>
  );
}

function ValueField({
  text,
  onChange,
  bad,
}: {
  text: string;
  onChange: (t: string) => void;
  bad: boolean;
}) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-ink-600 text-sm font-semibold">
        Valeur
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={text}
        aria-invalid={bad || undefined}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'text-ink-900 focus:ring-primary-400 block h-11 w-40 rounded-xl border bg-transparent px-3 text-lg tabular-nums outline-none focus:ring-2',
          bad ? 'border-error-500' : 'border-ink-200',
        )}
      />
      {bad && (
        <p className="text-error-700 text-sm">
          {frenchSpaces('Écris un nombre positif : 3,25 ou 1200 (6 décimales au plus).')}
        </p>
      )}
    </div>
  );
}

function Converter({
  kind,
  onSend,
  sendCost,
}: {
  kind: Kind;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const k = kindOf(kind);
  const [v0, f0, t0] = DEFAULTS[kind];
  const [text, setText] = useState(v0);
  const [fromId, setFrom] = useState(f0);
  const [toId, setTo] = useState(t0);
  const from = k.units.find((u) => u.id === fromId)!;
  const to = k.units.find((u) => u.id === toId)!;
  const d = parseDec(text);
  const res = d ? convert(d, from, to) : null;
  const need = d ? span(d, from, to) : undefined;
  const wide = columns(k, need);
  // au-delà de MAX_COLUMNS colonnes, on ne pose plus le nombre
  const cols = wide.length <= MAX_COLUMNS ? wide : columns(k);
  const cells = d ? placeDigits(d, from, to, cols) : null;
  const plain = res ? decText(res, '') : '';
  // en-tête : les colonnes consécutives d'une même unité sous un seul nom
  const groups: Array<{ unit: UnitDef | null; span: number; key: number }> = [];
  for (const c of cols) {
    const g = groups[groups.length - 1];
    if (g && g.unit && g.unit === c.group) g.span++;
    else groups.push({ unit: c.group, span: 1, key: c.exp });
  }
  const hasAlias = k.units.some((u) => u.alias);
  const cellW = Math.max(24, Math.min(40, Math.floor(336 / cols.length)));
  const anyAdded = !!cells?.some((c) => c.added);

  return (
    <div className="space-y-4">
      <ValueField text={text} onChange={setText} bad={!d} />
      <div className="space-y-1">
        <p className="text-ink-600 text-sm font-semibold">De</p>
        <UnitChips units={k.units} value={from.id} onChange={setFrom} label="Unité de départ" />
      </div>
      <div className="flex items-center gap-2">
        <IconButton
          label="Échanger les unités"
          onClick={() => {
            setFrom(to.id);
            setTo(from.id);
            if (res) setText(decText(res, ''));
          }}
        >
          <ArrowLeftRight className="h-5 w-5" aria-hidden />
        </IconButton>
        <span className="text-ink-500 text-sm">Échanger</span>
      </div>
      <div className="space-y-1">
        <p className="text-ink-600 text-sm font-semibold">Vers</p>
        <UnitChips units={k.units} value={to.id} onChange={setTo} label="Unité d’arrivée" />
      </div>

      {d && res && (
        <div className="flex flex-wrap items-center gap-2" data-testid="uc-result">
          <p className="font-display text-ink-900 min-w-0 flex-1 text-xl font-bold">
            {`${decText(d)}${NBSP}${from.label} = ${decText(res)}${NBSP}${to.label}`}
          </p>
          {onSend && (
            <SendToSheetButton
              label={plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: [plain] })}
            />
          )}
          <p className="sr-only" aria-live="polite">
            {`${decText(d)} ${unitName(from, d)}, c’est ${decText(res)} ${unitName(to, res)}`}
          </p>
        </div>
      )}

      {d && (
        <div className="border-ink-100 overflow-x-auto rounded-2xl border" data-testid="uc-table">
          <table className="border-collapse text-center">
            <caption className="sr-only">Tableau de conversion des {k.title.toLowerCase()}</caption>
            <thead>
              <tr>
                {groups.map((g) => (
                  <th
                    key={g.key}
                    scope="col"
                    colSpan={g.span}
                    className={cn(
                      'border-ink-200 border-x px-0.5 py-1 text-xs font-bold',
                      g.unit?.id === to.id
                        ? 'bg-primary-50 text-primary-700'
                        : g.unit?.id === from.id
                          ? 'bg-ink-100 text-ink-800'
                          : 'text-ink-600',
                    )}
                  >
                    {g.unit?.label ?? ''}
                  </th>
                ))}
              </tr>
              {hasAlias && (
                <tr>
                  {cols.map((c) => (
                    <th
                      key={c.exp}
                      scope="col"
                      className={cn(
                        'border-ink-100 border-x px-1 py-0.5 text-xs font-semibold',
                        c.alias?.id === to.id
                          ? 'bg-primary-50 text-primary-700'
                          : c.alias?.id === from.id
                            ? 'bg-ink-100 text-ink-800'
                            : 'text-ink-500',
                      )}
                    >
                      {c.alias?.label ?? ''}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              <tr>
                {cols.map((c, i) => {
                  const cell = cells?.[i];
                  const isFrom = c.exp === from.pos;
                  const isTo = c.exp === to.pos;
                  // trait plein entre deux unités, pointillé à l'intérieur d'une unité
                  const lastOfUnit = !c.group || c.group.pos === c.exp || i === cols.length - 1;
                  return (
                    <td
                      key={c.exp}
                      style={{ width: cellW, minWidth: cellW }}
                      className={cn(
                        'border-ink-100 relative h-11 border-t text-lg font-bold tabular-nums',
                        lastOfUnit ? 'border-ink-200 border-r' : 'border-r border-dashed',
                        cell?.added ? 'text-primary-700' : 'text-ink-900',
                      )}
                    >
                      {cell?.digit ?? ''}
                      {(isFrom || isTo) && (
                        <span
                          aria-hidden
                          className={cn(
                            'absolute -right-1 bottom-0.5 text-xl leading-none',
                            isTo ? 'text-primary-600 font-black' : 'text-ink-400',
                          )}
                        >
                          ,
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {d && !cells && (
        <Note>
          {frenchSpaces(
            'Le nombre dépasse du tableau : le résultat est juste, mais trop long pour y tenir.',
          )}
        </Note>
      )}
      {d && (
        <Note live={false}>
          {frenchSpaces(
            `${ruleText(k, from, to)}${cells ? ` La virgule grise est celle de départ, la bleue celle d’arrivée${anyAdded ? ' ; les zéros ajoutés sont en bleu' : ''}.` : ''}`,
          )}
        </Note>
      )}
      {kind === 'volume' && (
        <p className="text-ink-600 text-sm">
          {frenchSpaces('Repère : 1 L = 1 dm³ et 1 mL = 1 cm³ (un cube de 1 cm de côté).')}
        </p>
      )}
      {kind === 'area' && (
        <p className="text-ink-600 text-sm">
          {frenchSpaces('Repère : 1 ha = 1 hm², 1 a = 1 dam², 1 ca = 1 m².')}
        </p>
      )}
    </div>
  );
}

function SpeedConverter({ onSend, sendCost }: Pick<ToolProps, 'onSend' | 'sendCost'>) {
  const [text, setText] = useState('36');
  const [from, setFrom] = useState<Speed>('kmh');
  const d = parseDec(text);
  const r = d ? speedConvert(d, from) : null;
  const exact = r ? fracToDec(r.p, r.q) : null;
  const v = d ? decText(d) : '';
  const resText = r ? (exact ? decText(exact) : `${r.p}/${r.q} ≈ ${approxText(r.p, r.q)}`) : '';
  const toUnit = from === 'kmh' ? 'm/s' : 'km/h';
  const fromUnit = from === 'kmh' ? 'km/h' : 'm/s';
  const meters = d ? decText({ m: d.m, exp: d.exp + 3 }) : '';
  const steps =
    from === 'kmh'
      ? [
          `${v} km/h, c’est ${v} km parcourus en 1 h.`,
          `${v} km = ${meters} m et 1 h = 3 600 s : ${meters} m en 3 600 s.`,
          `En 1 s : ${meters} ÷ 3 600 = ${resText} m. Donc ${v} km/h = ${resText} m/s.`,
        ]
      : [
          `${v} m/s, c’est ${v} m parcourus en 1 s.`,
          `En 1 h = 3 600 s : ${v} × 3 600 m, soit ${d ? decText({ m: d.m * 36n, exp: d.exp + 2 }) : ''} m.`,
          `En km : ÷ 1 000. Donc ${v} m/s = ${resText} km/h.`,
        ];
  return (
    <div className="space-y-4">
      <ValueField text={text} onChange={setText} bad={!d} />
      <Segmented
        ariaLabel="Sens"
        value={from}
        options={[
          { value: 'kmh', label: 'km/h → m/s' },
          { value: 'ms', label: 'm/s → km/h' },
        ]}
        onChange={setFrom}
      />
      {r && (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="uc-speed">
            <p className="font-display text-ink-900 min-w-0 flex-1 text-xl font-bold">
              {`${v}${NBSP}${fromUnit} = ${resText}${NBSP}${toUnit}`}
            </p>
            {onSend && exact && (
              <SendToSheetButton
                label={decText(exact, '')}
                cost={sendCost}
                onClick={() => onSend({ atoms: [decText(exact, '')] })}
              />
            )}
          </div>
          <ol className="text-ink-800 list-decimal space-y-1 pl-5 text-sm" data-testid="uc-steps">
            {steps.map((s) => (
              <li key={s}>{frenchSpaces(s)}</li>
            ))}
          </ol>
          <Note>
            {frenchSpaces(
              'Retenir : pour passer des km/h aux m/s, on divise par 3,6 ; des m/s aux km/h, on multiplie par 3,6.',
            )}
          </Note>
        </>
      )}
    </div>
  );
}

export function UnitConverterTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = Math.min(5, Math.max(1, schoolLevelRank(level) ?? 5));
  const options: Array<{ value: Mode; label: string }> = [
    ...KINDS.filter((k) => k.minRank <= rank).map((k) => ({ value: k.id as Mode, label: k.title })),
    ...(rank >= 3 ? [{ value: 'speed' as Mode, label: 'Vitesses' }] : []),
  ];
  const [mode, setMode] = useState<Mode>('length');
  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Grandeur" value={mode} options={options} onChange={setMode} />
      {mode === 'speed' ? (
        <SpeedConverter onSend={onSend} sendCost={sendCost} />
      ) : (
        <Converter key={mode} kind={mode} onSend={onSend} sendCost={sendCost} />
      )}
    </div>
  );
}
